import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { StringParam, useQueryParams, withDefault } from 'use-query-params';

import { useAuth } from 'contexts/auth';
import { useAskKlueLog } from 'hooks/dashboard/use-ask-klue-log';
import { AnalyticsAction } from 'lib/analytics';
import {
  setAskKlueAppType,
  setAskKlueShowModal as setShowModal,
  setAskKlueThreadId,
  useAskKlueShowModal,
  setAskKlueSidebarOpen,
  setAskKlueIsModernThread,
  setAskKluePendingQuestion,
  setAskKlueInitialScreen,
} from 'store/views/dashboard';

import type { AskKlueEvent } from 'hooks/dashboard/use-ask-klue-log/useAskKlueLog';
import type {
  AskKlueAppType,
  AskKlueContextPageType,
} from 'store/api/ask-klue';

const QueryParamConfig = {
  askKlueVisibilityGroupId: withDefault(StringParam, undefined),
  askKlueSessionId: withDefault(StringParam, undefined),
  askKlueCategory: withDefault(StringParam, undefined),
  askKlueSourceApi: withDefault(StringParam, undefined),
};

function getContextPage(location: ReturnType<typeof useLocation>) {
  let contextPage: AskKlueContextPageType;
  if (location.pathname.includes('battlecard')) {
    contextPage = 'battlecard';
  } else {
    contextPage = 'dashboard';
  }
  return contextPage;
}

function useAskKlue() {
  const [queryParams, setQueryParams] = useQueryParams(QueryParamConfig);
  const { isCurator, isAskKlueDashboardPageEnabled } = useAuth();
  const showModal = useAskKlueShowModal();
  const logAskKlueEvent = useAskKlueLog();
  const location = useLocation();
  const contextPage = getContextPage(location);

  const visibilityGroupId = useMemo(() => {
    if (!isCurator) return undefined;

    return queryParams?.askKlueVisibilityGroupId
      ? parseInt(queryParams?.askKlueVisibilityGroupId)
      : undefined;
  }, [queryParams, isCurator]);

  // The navbar, outer prompt strip and wizard share this handler. It hands
  // the question to the mounted chat, which sends it once it is ready.
  const handleAskKlue = (
    question: string | null,
    logEventType: AskKlueEvent['type'] = AnalyticsAction.ask,
  ): void => {
    if (!question) return;
    if (!showModal) {
      openModal();
    }

    setAskKlueInitialScreen(false);
    // Log at ask time. Logging at drain time would lose the event whenever
    // the question is cancelled before it sends.
    logAskKlueEvent({
      askKlueId: '',
      eventType: logEventType,
      text: question,
    });
    setAskKluePendingQuestion(question);
  };

  const openModal = useCallback(
    (appType?: AskKlueAppType) => {
      if (
        isAskKlueDashboardPageEnabled &&
        location.pathname.includes('ask-klue')
      ) {
        return;
      }
      setShowModal(true);
      logAskKlueEvent({
        askKlueId: '',
        eventType: AnalyticsAction.openModal,
        appType,
      });
    },
    [logAskKlueEvent, isAskKlueDashboardPageEnabled, location.pathname],
  );

  const verifyAskKlueData = () => {
    setAskKlueAppType('chat_stream');

    if (queryParams.askKlueSessionId && queryParams.askKlueCategory) {
      openModal('chat_stream');
      setAskKlueSidebarOpen(true);
    }
  };

  const clearAskKlueSession = () => {
    setAskKlueThreadId(undefined);
    setAskKlueIsModernThread(false);
  };

  const closeModal = useCallback(() => {
    setAskKluePendingQuestion(undefined);
    setQueryParams(
      {
        askKlueSessionId: undefined,
        askKlueCategory: undefined,
        askKlueSourceApi: undefined,
      },
      'replaceIn',
    );
    setAskKlueAppType('chat_stream');
    setShowModal(false);
    setAskKlueSidebarOpen(false);
    logAskKlueEvent({
      askKlueId: '',
      eventType: AnalyticsAction.closeModal,
    });
    clearAskKlueSession();
  }, [setQueryParams, logAskKlueEvent]);

  return {
    showModal,
    visibilityGroupId,
    contextPage,
    askKlueQueryParams: queryParams,
    setAskKlueQueryParams: setQueryParams,
    setShowModal,
    closeModal,
    openModal,
    handleAskKlue,
    verifyAskKlueData,
    clearAskKlueSession,
  };
}

export default useAskKlue;
